import { Component, OnInit, OnDestroy } from '@angular/core'
import {
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  Subject,
  takeUntil,
} from 'rxjs'
import { NgxSpinnerService } from 'ngx-spinner'

// SDK
import { KEYS_TO_SYNC, AuthStoreService } from '@evologi/shared/auth-store'
import {
  AuthNotification,
  AuthRepository,
  KeyboardShortcutsService,
} from '@evologi/shared/data-access-api'
import { VersionRoutingService } from '../services/version-routing.service'

@Component({
  selector: 'op-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  public loading = false

  // Observables
  private destroy$: Subject<boolean> = new Subject<boolean>()

  constructor(
    private authStoreService: AuthStoreService,
    private authRepository: AuthRepository,
    private spinner: NgxSpinnerService,
    private keyboardShortcuts: KeyboardShortcutsService,
    private versionRoutingService: VersionRoutingService,
  ) {}

  // Lifecycle

  ngOnInit(): void {
    this.keyboardShortcuts.init()
    this.initStore()
  }

  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.unsubscribe()
  }

  // Utilities

  initStore() {
    // Loading management
    this.authStoreService
      .isLoading$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoading) =>
        isLoading ? this.spinner.show() : this.spinner.hide(),
      )

    // Local Storage
    fromEvent(window, 'storage')
      .pipe(
        takeUntil(this.destroy$),
        filter((event: any) => KEYS_TO_SYNC.includes(event.key)),
      )
      .subscribe((event) =>
        this.authStoreService.syncStore(event.key as string),
      )

    // Notification
    this.authStoreService
      .getNotification$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (notification) => notification && this.parseNotification(notification),
      )

    this.authStoreService
      .getAuthenticatedData$()
      .pipe(
        takeUntil(this.destroy$),
        map((authData) => authData.user._id),
        distinctUntilChanged(),
      )
      .subscribe((userId) =>
        this.versionRoutingService.redirectIfNeeded(userId),
      )
  }

  // Utilities

  parseNotification(notification: AuthNotification) {
    if (notification.type === 'notification') {
      this.authRepository.notify(notification)
    }
  }
}
